/**
 * Options (storefront side): one selector per option of the product — text
 * buttons, color swatches (hex), image swatches, or both — above the buy
 * button, and the client mirror of the server's pricing.
 *
 * The server sends (aldoo-na/store/product/extensions/options publicFields):
 *
 *   product.options   [{ key, label, display, required, variantAxis,
 *                        values: [{ key, label, hex?, image?, priceDelta? }] }]
 *   product.variants  [{ sku, choices: { size: "M" }, enabled, inStock, stock?, price?, images? }]
 *
 * The buyer's picks live in the page's selection as `choices` ({ size: "M" }).
 * A variant-axis value no enabled, in-stock variant carries (given the other
 * axes picked) is shown struck and cannot be picked. The line the selection
 * resolves to carries the variant's `sku` and, when it has any, its `images`
 * (a gallery may show them). `price` is display only: the server prices the
 * line again (resolveLine) — the variant's own price or the product's, plus
 * the deltas of the customizations; the page takes its discount off the whole.
 */
import React from "react"
import { imageProps } from "aldoo-ra/CMS/url"

const isRecord = (value) => !!value && typeof value === "object" && !Array.isArray(value)
const optionsOf = (product) => (Array.isArray(product?.options) ? product.options.filter((option) => option?.key && Array.isArray(option.values)) : [])
const variantsOf = (product) => (Array.isArray(product?.variants) ? product.variants.filter((variant) => variant?.sku) : [])
const choicesOf = (selection) => (isRecord(selection?.choices) ? selection.choices : {})
const axesOf = (options) => options.filter((option) => option.variantAxis)
const needed = (option) => option.required !== false || !!option.variantAxis

/** The variant the choices name on every axis, or null. */
const variantFor = (product, axes, choices) => {
  if (!axes.length || !axes.every((axis) => choices[axis.key])) return null
  return variantsOf(product).find((variant) => axes.every((axis) => String(variant.choices?.[axis.key] ?? "") === String(choices[axis.key]))) || null
}

const sellable = (variant) => !!variant && variant.enabled !== false && variant.inStock !== false

/**
 * May this axis value be picked: some sellable variant carries it, with the
 * other axes as picked so far. A product with no variants listed has nothing
 * to rule a value out with.
 */
const valueAvailable = (product, axes, option, value, choices) => {
  const variants = variantsOf(product)
  if (!variants.length) return true
  return variants.some((variant) => {
    if (!sellable(variant)) return false
    if (String(variant.choices?.[option.key] ?? "") !== value) return false
    return axes.every((axis) => axis.key === option.key || !choices[axis.key] || String(variant.choices?.[axis.key] ?? "") === String(choices[axis.key]))
  })
}

/** Is every required option picked, and the picked variant there to buy? */
export const isComplete = (product, selection) => {
  const options = optionsOf(product)
  if (!options.length) return true
  const choices = choicesOf(selection)
  if (!options.every((option) => !needed(option) || choices[option.key])) return false
  const axes = axesOf(options)
  if (!axes.length) return true
  return sellable(variantFor(product, axes, choices))
}

/** The required options not picked yet: [{ key, label }] (the buy button names them). */
export const missing = (product, selection) => {
  const choices = choicesOf(selection)
  return optionsOf(product)
    .filter((option) => needed(option) && !choices[option.key])
    .map((option) => ({ key: option.key, label: option.label || option.key }))
}

/** The client mirror of resolveLine: the choices, their labels, the variant's sku and price, the deltas. */
export const price = (product, selection, line) => {
  const options = optionsOf(product)
  if (!options.length) return line
  const chosen = choicesOf(selection)
  const choices = {}
  const choiceLabels = {}
  let delta = 0
  for (const option of options) {
    const key = chosen[option.key]
    if (!key) continue
    const value = option.values.find((entry) => entry.key === key)
    if (!value) throw new Error(`${option.key} has no value ${key}`)
    choices[option.key] = key
    choiceLabels[option.key] = { label: option.label || option.key, value: value.label || value.key }
    if (!option.variantAxis && Number.isFinite(value.priceDelta)) delta += Math.round(value.priceDelta * 100)
  }
  const variant = variantFor(product, axesOf(options), choices)
  let amount = line.amount
  if (Number.isFinite(amount)) {
    if (variant && Number.isFinite(variant.price)) amount = Math.round(variant.price * 100)
    amount += delta
  }
  return {
    ...line,
    amount,
    sku: variant?.sku || "",
    choices: { ...(line.choices || {}), ...choices },
    choiceLabels: { ...(line.choiceLabels || {}), ...choiceLabels },
    ...(variant?.images?.length ? { images: variant.images } : {}),
  }
}

// out of stock is marked by the strike, not by words: the framework has no text of its own here
const Strike = () => <span aria-hidden className="absolute inset-0 pointer-events-none bg-[linear-gradient(to_top_right,transparent_calc(50%-1px),currentColor,transparent_calc(50%+1px))] opacity-60" />

// the text buttons' picked / hovered color: the theme's primary, or (accent "text") the text color
const TEXT_RING = {
  primary: { selected: "border-primary text-primary ring-1 ring-primary", hover: "hover:border-primary" },
  text: { selected: "border-text text-text ring-1 ring-text", hover: "hover:border-text" },
}

/** One value as the option shows it: a text button, a color, an image, or an image with its color. */
function Value({ option, value, selected, available, onPick, accent = "primary" }) {
  const display = option.display || "text"
  const label = value.label || value.key
  const hasColor = (display === "color" || display === "color+image") && !!value.hex
  const hasImage = (display === "image" || display === "color+image") && !!value.image
  const state = !available ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
  const common = { type: "button", title: label, "aria-label": label, "aria-pressed": selected, disabled: !available, onClick: onPick, "data-testid": `option-value-${option.key}-${value.key}` }

  if (!hasColor && !hasImage) {
    const colors = TEXT_RING[accent] || TEXT_RING.primary
    const ring = selected ? colors.selected : `border-bordered text-text ${colors.hover}`
    return (
      <button {...common} className={`relative min-w-[44px] h-9 px-3 rounded-full border bg-transparent text-sm font-semibold leading-none whitespace-nowrap overflow-hidden transition-colors ${ring} ${state}`}>
        {label}
        {!available && <Strike />}
      </button>
    )
  }

  const ring = selected ? "ring-2 ring-offset-2 ring-primary" : "ring-1 ring-black/10 hover:ring-black/40"
  const shape = hasImage ? "w-14 h-14 rounded-md" : "w-9 h-9 rounded-full"
  return (
    <button {...common} className={`relative block p-0 border-0 ${shape} ${ring} ${state} transition-shadow`}>
      <span className={`relative block w-full h-full overflow-hidden ${hasImage ? "rounded-md" : "rounded-full"} bg-black/5`}>
        {hasImage && <img {...imageProps(value.image, "56px")} alt="" loading="lazy" className="w-full h-full object-cover" />}
        {!hasImage && <span className="absolute inset-0" style={{ background: value.hex }} />}
        {hasImage && hasColor && <span aria-hidden className="absolute right-1 bottom-1 w-3.5 h-3.5 rounded-full border border-white shadow" style={{ background: value.hex }} />}
        {!available && <Strike />}
      </span>
    </button>
  )
}

/** The selectors: per option its label with the picked value, then the values. `accent`: "primary" (default) or "text". */
export function OptionSelectors({ product, selection, setSelection, attempted = false, accent = "primary", className = "" }) {
  const options = optionsOf(product)
  if (!options.length) return null
  const choices = choicesOf(selection)
  // Buy was pressed without them: the required options not picked are marked
  const unpicked = (option) => attempted && needed(option) && !choices[option.key]
  const axes = axesOf(options)

  const pick = (option, key) =>
    setSelection((current) => {
      const before = choicesOf(current)
      const next = { ...before }
      // picking the picked value of an optional option takes it back
      if (before[option.key] === key && !needed(option)) delete next[option.key]
      else next[option.key] = key
      return { ...(current || {}), choices: next }
    })

  return (
    <div className={`flex flex-col gap-4 ${className}`} data-testid="option-selectors">
      {options.map((option) => {
        const picked = option.values.find((value) => value.key === choices[option.key])
        return (
          <div key={option.key} className={`flex flex-col gap-2 rounded-lg transition-shadow ${unpicked(option) ? "ring-2 ring-error ring-offset-4" : ""}`} data-testid={`option-${option.key}`} data-display={option.display || "text"} data-missing={unpicked(option) || undefined}>
            <div className={`text-sm ${unpicked(option) ? "text-error" : ""}`}>
              <span className="font-medium">{option.label || option.key}</span>
              {picked && <span className="opacity-70">: {picked.label || picked.key}</span>}
            </div>
            <ul className="flex flex-wrap gap-2 list-none m-0 p-0">
              {option.values.map((value) => {
                const available = !option.variantAxis || valueAvailable(product, axes, option, value.key, choices)
                return (
                  <li key={value.key}>
                    <Value option={option} value={value} accent={accent} selected={choices[option.key] === value.key} available={available} onPick={() => available && pick(option, value.key)} />
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </div>
  )
}

export const StorefrontExtension = {
  id: "options",
  label: "Options",
  slots: { selectors: OptionSelectors },
  isComplete,
  missing,
  price,
}

export default StorefrontExtension
